/**
 * Application Configuration
 * Central configuration for zoom, canvas, storage, and history settings
 */

import type { ZoomConfig, CanvasConfig } from '@/types';

// ============================================================
// ZOOM CONFIGURATION
// ============================================================

/** Zoom limits and behavior settings */
export const ZOOM_CONFIG: ZoomConfig = {
  /** Minimum zoom level (25%) */
  min: 0.25,
  /** Maximum zoom level (400%) */
  max: 4,
  /** Default zoom level (100%) */
  default: 1,
  /** Zoom step for button controls */
  step: 0.1,
  /** Sensitivity for mouse wheel zoom */
  wheelSensitivity: 0.001,
};

// ============================================================
// CANVAS CONFIGURATION
// ============================================================

/** Canvas dimensions and grid settings */
export const CANVAS_CONFIG: CanvasConfig = {
  /** Canvas width in pixels */
  width: 4000,
  /** Canvas height in pixels */
  height: 3000,
  /** Grid cell size in pixels */
  gridSize: 40,
};

// ============================================================
// STORAGE CONFIGURATION
// ============================================================

/** Local storage settings */
export const STORAGE_CONFIG = {
  /** Prefix for all localStorage keys */
  prefix: 'MangoGrid8_',
  /** Maximum number of saved projects */
  maxProjects: 10,
  /** Auto-save delay in milliseconds */
  autoSaveDelay: 2000,
  /** Current storage version */
  version: '8.0.0',
} as const;

// ============================================================
// HISTORY CONFIGURATION
// ============================================================

/** Undo/redo history settings */
export const HISTORY_CONFIG = {
  /** Maximum number of history states to keep */
  maxSize: 50,
} as const;

// ============================================================
// PYODIDE CONFIGURATION
// ============================================================

/** Pyodide Python runtime settings */
export const PYODIDE_CONFIG = {
  /** CDN URL for Pyodide */
  indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/',
  /** Required packages */
  packages: ['micropip', 'numpy', 'pandas'],
  /** MangoGrid package name for micropip */
  MangoGridPackage: 'MangoGrid',
} as const;

// ============================================================
// UI CONFIGURATION
// ============================================================

/** Toast notification settings */
export const TOAST_CONFIG = {
  /** Duration to show toast in milliseconds */
  duration: 3000,
  /** Maximum number of simultaneous toasts */
  maxToasts: 5,
} as const;

/** Bottom panel settings */
export const PANEL_CONFIG = {
  /** Default height of bottom panel */
  bottomPanelHeight: 220,
  /** Toolbar width */
  toolbarWidth: 300,
} as const;

// ============================================================
// COMBINED CONFIG (Legacy support)
// ============================================================

/** Combined configuration object for backward compatibility */
export const CONFIG = {
  zoom: ZOOM_CONFIG,
  canvas: CANVAS_CONFIG,
  storage: STORAGE_CONFIG,
  history: HISTORY_CONFIG,
  pyodide: PYODIDE_CONFIG,
  toast: TOAST_CONFIG,
  panel: PANEL_CONFIG,
} as const;

export type Config = typeof CONFIG;

